import { TestCallerType } from "@swivl-mono/types";

import { APIService } from "../Service/API.Service";

// Mirrors apps/server/src/API/TestCaller/TestCaller.Types.ts

export interface TestCallerCarrier {
  userId: number;
  name: string | null;
  isMappingTarget: boolean;
}

export interface TestCallerStatusResponse {
  phone: string;
  activeMapping: {
    id: number;
    userId: number;
    userName: string | null;
    appliedAt: string;
    appliedByMemberId: number;
    appliedByName: string | null;
    /** Temporary identity override in effect (restored on undo/reset/re-apply). */
    overrideName: string | null;
    overrideEmail: string | null;
    originalName: string | null;
    originalEmail: string | null;
  } | null;
  carriers: TestCallerCarrier[];
  orgAllowed: boolean;
}

export interface TestCallerCandidate {
  userId: number;
  name: string | null;
  email: string | null;
  activeLeaseCount: number;
  totalBalance: number;
  pastDue: boolean;
  locationNames: string[];
  lastBotActivityAt: string | null;
}

export interface TestCallerCandidatesResponse {
  type: TestCallerType;
  candidates: TestCallerCandidate[];
}

export interface TestCallerActionResponse {
  success: boolean;
  message: string;
  status: TestCallerStatusResponse;
}

/** Test Caller admin tool (ENG-2866) — attach a tester's phone to a real account. */
export class TestCallerAPI {
  static async getStatus(orgId: number, phone: string): Promise<TestCallerStatusResponse> {
    return APIService.Server.get<TestCallerStatusResponse>(`/test-caller/org/${orgId}/status?phone=${encodeURIComponent(phone)}`);
  }

  static async getCandidates(orgId: number, type: TestCallerType): Promise<TestCallerCandidatesResponse> {
    return APIService.Server.get<TestCallerCandidatesResponse>(`/test-caller/org/${orgId}/candidates?type=${encodeURIComponent(type)}`);
  }

  static async apply(
    orgId: number,
    phone: string,
    userId: number,
    /** Optional temporary identity override — reverted on undo/reset/re-apply. */
    override?: { name?: string; email?: string }
  ): Promise<TestCallerActionResponse> {
    return APIService.Server.post<TestCallerActionResponse>(`/test-caller/org/${orgId}/apply`, {
      phone,
      userId,
      name: override?.name,
      email: override?.email
    });
  }

  static async undo(orgId: number, phone: string): Promise<TestCallerActionResponse> {
    return APIService.Server.post<TestCallerActionResponse>(`/test-caller/org/${orgId}/undo`, { phone });
  }

  /** Reset to a brand-new caller: detach everywhere + wipe pure test identities. */
  static async reset(orgId: number, phone: string): Promise<TestCallerActionResponse> {
    return APIService.Server.post<TestCallerActionResponse>(`/test-caller/org/${orgId}/reset`, { phone });
  }
}
